// Estado, fetch y handlers del listado de devoluciones (desktop + móvil).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import { DEFAULT_PER_PAGE } from '../../../constants/constant'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../../components/MuiTablePagination'
import usePagination from '../../../layout/usePagination'
import { layoutMobileMediaQuery } from '../../../layout/layoutConstants'
import dayjs from '../../../utils/dayjsConfig'
import {
  BASE_STATUS_FILTER_LABELS,
  PROCESADOR_STATUS_FILTER_LABELS,
  REFUND_LABEL_TO_STATUS_VALUE,
  REFUND_STATUS_FILTER_OPTIONS
} from '../../constants/refundStatus'
import { getUserWarehouse } from '../../../rbac/utils/rbacCookies'
import {
  isRefundBaseListView,
  isRefundProcesadorView,
  showRefundNcrColumn
} from '../../../global_context/capabilities/refundCapabilities'
import {
  REFUND_FILTER_KEYS,
  buildAppliedChipLabel,
  buildAppliedDateRange,
  buildSelectValue,
  countSelectedGroups,
  createEmptyDateRange,
  createEmptyDraftFilterSelections,
  dateRangeToPickerValue,
  dateRangeToQuery,
  formatDateRangeSummary,
  getSelectValues,
  normalizeDateRange,
  pickerValueToDateRange
} from '../../helpers/refundMobileFilters'
import {
  FILTER_OPTIONS_PAGE_SIZE,
  fetchRefundFilterOptions
} from '../../helpers/refundFilterOptions'
import {
  ASYNC_FILTER_KEYS,
  FILTER_KEY_TO_DIMENSION,
  FILTER_SEARCH_MIN_CHARS,
  buildFiltersQuery,
  createInitialFilterOptionsByKey,
  createProcesadorDefaultSelections,
  isLockedWarehouseFilterKey
} from '../../helpers/refundIndexFilters'

const MOBILE_SEARCH_DEBOUNCE_MS = 450

export default function useRefundIndex() {
  const procesadorView = isRefundProcesadorView()
  const baseListView = isRefundBaseListView()
  const showNcrColumn = showRefundNcrColumn()
  const userWarehouse = getUserWarehouse()
  const userWarehouseUid = userWarehouse?.uid
  const userWarehouseName = userWarehouse?.name || userWarehouseUid
  const warehouseLocked = Boolean(procesadorView && userWarehouseUid)
  const lockedWarehouseOptions = useMemo(() => {
    if (!warehouseLocked) return []
    return [{ value: userWarehouseUid, label: userWarehouseName }]
  }, [warehouseLocked, userWarehouseUid, userWarehouseName])

  const [isFetching, setIsFetching] = useState(true)
  const [data, setData] = useState(null)
  const [filterOptionsByKey, setFilterOptionsByKey] = useState(createInitialFilterOptionsByKey)
  const [allSelectedOptions, setAllSelectedOptions] = useState(() =>
    procesadorView ? createProcesadorDefaultSelections() : new Map()
  )
  const [filterQuery, setFilterQuery] = useState(() =>
    procesadorView ? buildFiltersQuery(createProcesadorDefaultSelections()) : ''
  )
  const [dateQuery, setDateQuery] = useState(null)
  const [selectedDates, setSelectedDates] = useState(null)
  const { pagination, setPagination, handlePagination } = usePagination({
    per_page: DEFAULT_PER_PAGE,
    total_count: 0
  })
  const [labelToIdMapping, setLabelToIdMapping] = useState(new Map())
  const [idToLabelMapping, setIdToLabelMapping] = useState(new Map())
  const [searchInput, setSearchInput] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [draftFilterSelections, setDraftFilterSelections] = useState(() => {
    const emptyDraft = createEmptyDraftFilterSelections()
    if (!(procesadorView && userWarehouseUid)) return emptyDraft

    return {
      ...emptyDraft,
      [REFUND_FILTER_KEYS.WAREHOUSE]: buildSelectValue([userWarehouseUid])
    }
  })
  const [draftDateRange, setDraftDateRange] = useState(createEmptyDateRange())
  const [expandedRefundUuid, setExpandedRefundUuid] = useState(null)

  const abortControllerRef = useRef(new AbortController())
  const mobileSearchTimerRef = useRef(null)
  const filterRequestIdRef = useRef({
    [REFUND_FILTER_KEYS.CLIENT]: 0,
    [REFUND_FILTER_KEYS.USER]: 0,
    [REFUND_FILTER_KEYS.WAREHOUSE]: 0
  })
  const isMobileList = useMediaQuery(layoutMobileMediaQuery)

  useEffect(() => {
    if (MUI_TABLE_PAGINATION_OPTIONS.includes(pagination.per_page)) return

    setPagination((prevValue) => ({
      ...prevValue,
      per_page: DEFAULT_PER_PAGE,
      current_page: 1,
      total_pages: Math.ceil((prevValue.total_count || 0) / DEFAULT_PER_PAGE) || 1
    }))
  }, [pagination.per_page, setPagination])

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortControllerRef.current.abort('Fetch Aborted by User.')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      clearTimeout(mobileSearchTimerRef.current)
      abortControllerRef.current.abort('Component unmounted')
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const mergeOptionMappings = useCallback((options) => {
    setLabelToIdMapping((prevMapping) => {
      const nextMapping = new Map(prevMapping)
      options.forEach((option) => {
        nextMapping.set(option.label, option.value)
      })
      return nextMapping
    })

    setIdToLabelMapping((prevMapping) => {
      const nextMapping = new Map(prevMapping)
      options.forEach((option) => {
        nextMapping.set(option.value, option.label)
      })
      return nextMapping
    })
  }, [])

  const loadFilterOptions = useCallback(
    async (filterKey, { page = 1, q = '', append = false } = {}) => {
      const dimension = FILTER_KEY_TO_DIMENSION[filterKey]
      if (!dimension) return

      const requestId = (filterRequestIdRef.current[filterKey] || 0) + 1
      filterRequestIdRef.current[filterKey] = requestId

      setFilterOptionsByKey((prevState) => ({
        ...prevState,
        [filterKey]: {
          ...prevState[filterKey],
          loading: true,
          search: q
        }
      }))

      try {
        const response = await fetchRefundFilterOptions({
          dimension,
          page,
          perPage: FILTER_OPTIONS_PAGE_SIZE,
          q,
          signal: abortControllerRef.current.signal
        })

        if (filterRequestIdRef.current[filterKey] !== requestId) return

        const nextOptions = (response.result || []).map((option) => ({
          value: String(option.value || '').trim(),
          label: String(option.label || option.value || '').trim()
        }))

        mergeOptionMappings(nextOptions)

        setFilterOptionsByKey((prevState) => {
          const previousOptions = append ? prevState[filterKey].options : []
          const seenValues = new Set(previousOptions.map((option) => option.value))
          const mergedOptions = [...previousOptions]

          nextOptions.forEach((option) => {
            if (!option.value || seenValues.has(option.value)) return
            seenValues.add(option.value)
            mergedOptions.push(option)
          })

          const count = response.count || 0

          return {
            ...prevState,
            [filterKey]: {
              options: mergedOptions,
              page,
              count,
              hasMore: mergedOptions.length < count,
              loading: false,
              search: q
            }
          }
        })
      } catch (error) {
        if (abortControllerRef.current.signal.aborted || error?.name === 'AbortError') return
        if (filterRequestIdRef.current[filterKey] !== requestId) return

        console.error('Error loading refund filter options:', error)
        setFilterOptionsByKey((prevState) => ({
          ...prevState,
          [filterKey]: {
            ...prevState[filterKey],
            loading: false
          }
        }))
      }
    },
    [mergeOptionMappings]
  )

  const handleFilterSearchChange = useCallback(
    (filterKey, nextSearch) => {
      const normalizedSearch = String(nextSearch || '').trim()

      if (normalizedSearch === '') {
        loadFilterOptions(filterKey, { page: 1, q: '', append: false })
        return
      }

      if (normalizedSearch.length < FILTER_SEARCH_MIN_CHARS) {
        return
      }

      loadFilterOptions(filterKey, { page: 1, q: normalizedSearch, append: false })
    },
    [loadFilterOptions]
  )

  const handleFilterLoadMore = useCallback(
    (filterKey) => {
      const state = filterOptionsByKey[filterKey]
      if (!state || state.loading || !state.hasMore) return

      loadFilterOptions(filterKey, {
        page: state.page + 1,
        q: state.search,
        append: true
      })
    },
    [filterOptionsByKey, loadFilterOptions]
  )

  useEffect(() => {
    const statusLabels = procesadorView
      ? PROCESADOR_STATUS_FILTER_LABELS
      : baseListView
        ? BASE_STATUS_FILTER_LABELS
        : REFUND_STATUS_FILTER_OPTIONS
    const statusLabelToValue = REFUND_LABEL_TO_STATUS_VALUE
    setLabelToIdMapping((prevMapping) => {
      const nextMapping = new Map(prevMapping)
      statusLabels.forEach((label) => {
        nextMapping.set(label, statusLabelToValue[label])
      })
      if (userWarehouseUid) {
        nextMapping.set(userWarehouseName, userWarehouseUid)
      }
      return nextMapping
    })
    setIdToLabelMapping((prevMapping) => {
      const nextMapping = new Map(prevMapping)
      statusLabels.forEach((label) => {
        nextMapping.set(statusLabelToValue[label], label)
      })
      if (userWarehouseUid) {
        nextMapping.set(userWarehouseUid, userWarehouseName)
      }
      return nextMapping
    })
  }, [baseListView, procesadorView, userWarehouseName, userWarehouseUid])

  useEffect(() => {
    ASYNC_FILTER_KEYS.forEach((filterKey) => {
      if (isLockedWarehouseFilterKey(warehouseLocked, filterKey)) return
      loadFilterOptions(filterKey, { page: 1, q: '', append: false })
    })
  }, [loadFilterOptions, warehouseLocked])

  const statusFilterOptions = useMemo(() => {
    const labels = procesadorView
      ? PROCESADOR_STATUS_FILTER_LABELS
      : baseListView
        ? BASE_STATUS_FILTER_LABELS
        : REFUND_STATUS_FILTER_OPTIONS
    return labels.map((label) => ({
      label,
      value: REFUND_LABEL_TO_STATUS_VALUE[label]
    }))
  }, [baseListView, procesadorView])

  const filterConfigs = useMemo(
    () => [
      {
        key: REFUND_FILTER_KEYS.CLIENT,
        title: REFUND_FILTER_KEYS.CLIENT,
        options: filterOptionsByKey[REFUND_FILTER_KEYS.CLIENT].options,
        loading: filterOptionsByKey[REFUND_FILTER_KEYS.CLIENT].loading,
        hasMore: filterOptionsByKey[REFUND_FILTER_KEYS.CLIENT].hasMore
      },
      {
        key: REFUND_FILTER_KEYS.USER,
        title: REFUND_FILTER_KEYS.USER,
        options: filterOptionsByKey[REFUND_FILTER_KEYS.USER].options,
        loading: filterOptionsByKey[REFUND_FILTER_KEYS.USER].loading,
        hasMore: filterOptionsByKey[REFUND_FILTER_KEYS.USER].hasMore
      },
      {
        key: REFUND_FILTER_KEYS.WAREHOUSE,
        title: REFUND_FILTER_KEYS.WAREHOUSE,
        options: warehouseLocked
          ? lockedWarehouseOptions
          : filterOptionsByKey[REFUND_FILTER_KEYS.WAREHOUSE].options,
        loading: warehouseLocked
          ? false
          : filterOptionsByKey[REFUND_FILTER_KEYS.WAREHOUSE].loading,
        hasMore: warehouseLocked
          ? false
          : filterOptionsByKey[REFUND_FILTER_KEYS.WAREHOUSE].hasMore,
        pinnedOptions: lockedWarehouseOptions,
        skipAsync: warehouseLocked
      },
      {
        key: REFUND_FILTER_KEYS.STATUS,
        title: REFUND_FILTER_KEYS.STATUS,
        options: statusFilterOptions,
        maxVisibleOptions: 6
      }
    ],
    [filterOptionsByKey, lockedWarehouseOptions, statusFilterOptions, warehouseLocked]
  )

  const buildDraftSelectionsFromApplied = useCallback(
    () => ({
      [REFUND_FILTER_KEYS.CLIENT]: buildSelectValue(
        allSelectedOptions.get(REFUND_FILTER_KEYS.CLIENT)
      ),
      [REFUND_FILTER_KEYS.USER]: buildSelectValue(allSelectedOptions.get(REFUND_FILTER_KEYS.USER)),
      [REFUND_FILTER_KEYS.STATUS]: buildSelectValue(
        allSelectedOptions.get(REFUND_FILTER_KEYS.STATUS)
      ),
      [REFUND_FILTER_KEYS.WAREHOUSE]: buildSelectValue(
        allSelectedOptions.get(REFUND_FILTER_KEYS.WAREHOUSE)
      )
    }),
    [allSelectedOptions]
  )

  const syncMobileDraftWithApplied = useCallback(() => {
    setDraftFilterSelections(buildDraftSelectionsFromApplied())
    setDraftDateRange(pickerValueToDateRange(selectedDates))
  }, [buildDraftSelectionsFromApplied, selectedDates])

  const handleDesktopFilterChange = (filterKey, nextValue) => {
    if (isLockedWarehouseFilterKey(warehouseLocked, filterKey)) return

    const selectedIds = getSelectValues(nextValue)

    setAllSelectedOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      updatedOptions.set(filterKey, selectedIds)
      setFilterQuery(buildFiltersQuery(updatedOptions))
      return updatedOptions
    })

    setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))
  }

  const handleDateFilter = (datesObj) => {
    if (!datesObj || (Array.isArray(datesObj) && datesObj.length === 0)) {
      setSelectedDates(null)
      setDateQuery(null)
      setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))
      setTriggerSearch((prevValue) => !prevValue)
      return
    }

    const dates = Array.isArray(datesObj) ? datesObj : [datesObj]
    const parsedDates = dates
      .map((value) => {
        if (dayjs.isDayjs(value)) return value
        if (value?.unix != null) return dayjs.unix(value.unix)
        if (typeof value?.toDate === 'function') return dayjs(value.toDate())
        return dayjs(value)
      })
      .filter((value) => value.isValid())

    if (!parsedDates.length) return

    // One click => that day. Two clicks => ordered range (incl. reverse).
    const start = dayjs.min(parsedDates).startOf('day')
    const end = dayjs.max(parsedDates).endOf('day')

    setSelectedDates(dates)
    // YYYY-MM-DD avoids encoding issues; backend already expands to beginning/end of day.
    setDateQuery(`&start_date=${start.format('YYYY-MM-DD')}&end_date=${end.format('YYYY-MM-DD')}`)
    setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))
    setTriggerSearch((prevValue) => !prevValue)
  }

  const handleOpenMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(true)
  }

  const handleCloseMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(false)
  }

  const handleDraftSelectChange = (filterKey, nextValue) => {
    if (isLockedWarehouseFilterKey(warehouseLocked, filterKey)) return

    setDraftFilterSelections((prevValue) => ({
      ...prevValue,
      [filterKey]: buildSelectValue(getSelectValues(nextValue))
    }))
  }

  const buildDraftSelectionsForReset = useCallback(() => {
    const emptyDraft = createEmptyDraftFilterSelections()
    if (!warehouseLocked) return emptyDraft

    return {
      ...emptyDraft,
      [REFUND_FILTER_KEYS.WAREHOUSE]: buildSelectValue([userWarehouseUid])
    }
  }, [userWarehouseUid, warehouseLocked])

  const handleDraftDateChange = (boundary, nextValue) => {
    const normalizedValue = nextValue?.isValid?.() ? nextValue.startOf('day') : null

    setDraftDateRange((prevRange) =>
      normalizeDateRange({
        ...prevRange,
        [boundary]: normalizedValue
      })
    )
  }

  const clearDraftDateRange = () => {
    setDraftDateRange(createEmptyDateRange())
  }

  const clearSearchInput = () => {
    clearTimeout(mobileSearchTimerRef.current)
    setSearchInput('')
    setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))
    setTriggerSearch((prevValue) => !prevValue)
  }

  const resetReactFilters = () => {
    const nextSelections = procesadorView ? createProcesadorDefaultSelections() : new Map()
    setAllSelectedOptions(nextSelections)
    setFilterQuery(buildFiltersQuery(nextSelections))
    setDateQuery(null)
    setSelectedDates(null)
    setSearchInput('')
    setDraftFilterSelections(buildDraftSelectionsForReset())
    setDraftDateRange(createEmptyDateRange())
    setTriggerSearch((prevValue) => !prevValue)
  }

  const clearAllMobileFilters = () => {
    resetReactFilters()
    setIsMobileFiltersOpen(false)
  }

  const applyMobileFilters = () => {
    const nextSelectedFilterOptions = new Map()
    const nextDateRange = buildAppliedDateRange(draftDateRange)

    Object.values(REFUND_FILTER_KEYS).forEach((filterKey) => {
      if (isLockedWarehouseFilterKey(warehouseLocked, filterKey)) {
        nextSelectedFilterOptions.set(filterKey, [userWarehouseUid])
        return
      }

      nextSelectedFilterOptions.set(
        filterKey,
        [...getSelectValues(draftFilterSelections[filterKey])]
      )
    })

    setAllSelectedOptions(nextSelectedFilterOptions)
    setFilterQuery(buildFiltersQuery(nextSelectedFilterOptions))
    setSelectedDates(dateRangeToPickerValue(nextDateRange))
    setDateQuery(dateRangeToQuery(nextDateRange))
    setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))
    setIsMobileFiltersOpen(false)
  }

  const selectedDateRange = useMemo(() => pickerValueToDateRange(selectedDates), [selectedDates])

  const appliedFilterSelections = useMemo(
    () => ({
      [REFUND_FILTER_KEYS.CLIENT]: buildSelectValue(
        allSelectedOptions.get(REFUND_FILTER_KEYS.CLIENT)
      ),
      [REFUND_FILTER_KEYS.USER]: buildSelectValue(allSelectedOptions.get(REFUND_FILTER_KEYS.USER)),
      [REFUND_FILTER_KEYS.STATUS]: buildSelectValue(
        allSelectedOptions.get(REFUND_FILTER_KEYS.STATUS)
      ),
      [REFUND_FILTER_KEYS.WAREHOUSE]: buildSelectValue(
        allSelectedOptions.get(REFUND_FILTER_KEYS.WAREHOUSE)
      )
    }),
    [allSelectedOptions]
  )

  const appliedFilterGroupCount = useMemo(() => {
    const selectionsForCount = { ...appliedFilterSelections }

    if (warehouseLocked) {
      selectionsForCount[REFUND_FILTER_KEYS.WAREHOUSE] = buildSelectValue()
    }

    return countSelectedGroups(selectionsForCount, selectedDateRange)
  }, [appliedFilterSelections, selectedDateRange, warehouseLocked])

  const appliedFilterChips = useMemo(() => {
    const chips = []
    const clientValues = getSelectValues(appliedFilterSelections[REFUND_FILTER_KEYS.CLIENT])
    const userValues = getSelectValues(appliedFilterSelections[REFUND_FILTER_KEYS.USER])
    const statusValues = getSelectValues(appliedFilterSelections[REFUND_FILTER_KEYS.STATUS])
    const warehouseValues = warehouseLocked
      ? []
      : getSelectValues(appliedFilterSelections[REFUND_FILTER_KEYS.WAREHOUSE])
    const appliedDateRange = buildAppliedDateRange(selectedDateRange)

    if (clientValues.length > 0) {
      chips.push({
        key: REFUND_FILTER_KEYS.CLIENT,
        label: buildAppliedChipLabel({
          singularLabel: 'Cliente',
          pluralLabel: 'Clientes',
          values: clientValues,
          idToLabelMapping
        })
      })
    }

    if (userValues.length > 0) {
      chips.push({
        key: REFUND_FILTER_KEYS.USER,
        label: buildAppliedChipLabel({
          singularLabel: 'Creado por',
          pluralLabel: 'Creados por',
          values: userValues,
          idToLabelMapping
        })
      })
    }

    if (warehouseValues.length > 0) {
      chips.push({
        key: REFUND_FILTER_KEYS.WAREHOUSE,
        label: buildAppliedChipLabel({
          singularLabel: 'Bodega',
          pluralLabel: 'Bodegas',
          values: warehouseValues,
          idToLabelMapping
        })
      })
    }

    if (statusValues.length > 0) {
      chips.push({
        key: REFUND_FILTER_KEYS.STATUS,
        label: buildAppliedChipLabel({
          singularLabel: 'Estado',
          pluralLabel: 'Estados',
          values: statusValues,
          idToLabelMapping
        })
      })
    }

    if (appliedDateRange.start) {
      chips.push({
        key: 'date-range',
        label: `Fechas: ${formatDateRangeSummary(appliedDateRange, '')}`
      })
    }

    return chips
  }, [appliedFilterSelections, idToLabelMapping, selectedDateRange, warehouseLocked])

  // Bodega fija del procesador no cuenta como filtro aplicado (ni muestra "Restablecer").
  const isAnyFilterSelected = () =>
    Array.from(allSelectedOptions.entries()).some(([key, options]) => {
      if (isLockedWarehouseFilterKey(warehouseLocked, key)) return false
      return options.length > 0
    }) || dateQuery != null

  const fetchDataFromServer = useCallback(async () => {
    const perPage = pagination.per_page
    const page = pagination.current_page
    const normalizedSearchInput = searchInput.trim()
    const searchQuery = normalizedSearchInput
      ? `&q=${encodeURIComponent(normalizedSearchInput)}`
      : ''

    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/refunds?page=${page}&per_page=${perPage}${filterQuery || dateQuery || normalizedSearchInput ? '&filter_search=true' : ''}${filterQuery || ''}${dateQuery || ''}${searchQuery}`,
      {
        method: 'GET',
        signal: abortControllerRef.current.signal
      }
    )

    if (!response) return

    setPagination((prevValue) => ({
      ...prevValue,
      total_count: response.count,
      total_pages: Math.ceil(response.count / prevValue.per_page) || 1
    }))
    setData(response.table || [])
    setIsFetching(false)
  }, [
    dateQuery,
    filterQuery,
    pagination.current_page,
    pagination.per_page,
    searchInput,
    setPagination
  ])

  const handleMuiPageChange = (_event, nextPage) => {
    setPagination((prevValue) => ({ ...prevValue, current_page: nextPage }))
  }

  const handleMuiRowsPerPageChange = (_event, nextRowsPerPage) => {
    const safeRowsPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(nextRowsPerPage)
      ? nextRowsPerPage
      : DEFAULT_PER_PAGE

    setPagination((prevValue) => ({
      ...prevValue,
      per_page: safeRowsPerPage,
      current_page: 1,
      total_pages: Math.ceil((prevValue.total_count || 0) / safeRowsPerPage) || 1
    }))
  }

  useEffect(() => {
    const fetchAll = async () => {
      await fetchDataFromServer()
    }

    fetchAll()
  }, [pagination.current_page, pagination.per_page, filterQuery, dateQuery, triggerSearch])

  const handleSearch = (event) => {
    setSearchInput(event.target.value)
    setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))

    if (isMobileList) {
      clearTimeout(mobileSearchTimerRef.current)
      mobileSearchTimerRef.current = setTimeout(() => {
        setTriggerSearch((prevValue) => !prevValue)
      }, MOBILE_SEARCH_DEBOUNCE_MS)
    }
  }

  const handleEnterSearch = (event) => {
    if (event.key === 'Enter') {
      setPagination((prevValue) => ({ ...prevValue, current_page: 1 }))
      setTriggerSearch((prevValue) => !prevValue)
    }
  }

  const toggleRefundCardExpand = (uuid) => {
    setExpandedRefundUuid((prevValue) => (prevValue === uuid ? null : uuid))
  }

  return {
    isFetching,
    isMobileList,
    data,
    showNcrColumn,
    pagination,
    handlePagination,
    searchInput,
    filterConfigs,
    appliedFilterSelections,
    appliedFilterGroupCount,
    appliedFilterChips,
    selectedDates,
    isAnyFilterSelected: isAnyFilterSelected(),
    handleDesktopFilterChange,
    handleDateFilter,
    handleFilterSearchChange,
    handleFilterLoadMore,
    resetReactFilters,
    handleSearch,
    handleEnterSearch,
    clearSearchInput,
    handleMuiPageChange,
    handleMuiRowsPerPageChange,
    isMobileFiltersOpen,
    draftFilterSelections,
    draftDateRange,
    expandedRefundUuid,
    handleOpenMobileFilters,
    handleCloseMobileFilters,
    handleDraftSelectChange,
    handleDraftDateChange,
    clearDraftDateRange,
    clearAllMobileFilters,
    applyMobileFilters,
    toggleRefundCardExpand
  }
}
